import { useEffect, useState } from "react";
import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCcw, ShieldCheck, Send, Download } from "lucide-react";

/* ═════════════════════════════════════════════════════════════════════
   Legal Configuration Centre — admin editor for the four public-content
   slugs (legal-entity, faq, footer-links, cookie-config). No redeploy
   required to change what visitors see.
   ═════════════════════════════════════════════════════════════════════ */

const SLUGS = [
    { key: "legal-entity",  label: "Legal Entity",   type: "fields" },
    { key: "faq",           label: "FAQs",           type: "faq" },
    { key: "footer-links",  label: "Footer Links",   type: "footer" },
    { key: "cookie-config", label: "Cookie Config",  type: "cookie" },
    { key: "testimonial-request", label: "Testimonial Requests", type: "request" },
    { key: "consent-audit", label: "Consent Audit",  type: "audit" },
];

export default function LegalConfigCentre() {
    const [active, setActive] = useState(SLUGS[0].key);
    const [data, setData] = useState({});
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const r = await api.get(`/public-content/${active}`);
            setData(r.data || {});
        } catch { setData({}); }
        finally { setBusy(false); }
    };
    useEffect(() => { load(); }, [active]); // load when the active tab changes

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/public-content/${active}`, data);
            toast.success("Saved. Public site now shows your changes.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const spec = SLUGS.find((s) => s.key === active);

    return (
        <div className="max-w-[1200px] mx-auto" data-testid="legal-config-centre">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="eyebrow">Trust · Legal Configuration Centre</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Legal &amp; content settings.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Edit the legal-entity fields, footer links, FAQs and cookie categories used across
                        VAULT&apos;s public marketing site. Changes go live instantly — no redeploy.
                    </p>
                </div>
                <button className="inline-flex items-center gap-2 text-sm bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-full" onClick={load} disabled={busy} data-testid="lcc-reload">
                    <RefreshCcw size={13} /> Reload
                </button>
            </div>

            <div className="flex gap-2 mb-6 flex-wrap">
                {SLUGS.map((s) => (
                    <button key={s.key}
                            onClick={() => setActive(s.key)}
                            className={`text-sm px-4 py-1.5 rounded-full border ${active === s.key ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
                            data-testid={`lcc-tab-${s.key}`}
                    >{s.label}</button>
                ))}
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid={`lcc-panel-${active}`}>
                {spec?.type === "fields"  && <FieldsEditor data={data} setData={setData} />}
                {spec?.type === "faq"     && <FAQEditor    data={data} setData={setData} />}
                {spec?.type === "footer"  && <FooterEditor data={data} setData={setData} />}
                {spec?.type === "cookie"  && <CookieEditor data={data} setData={setData} />}
                {spec?.type === "request" && <TestimonialRequestPanel />}
                {spec?.type === "audit"   && <ConsentAuditPanel />}
            </div>

            {spec?.type !== "request" && spec?.type !== "audit" && (
            <div className="mt-6 flex justify-end">
                <button className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-stone-800 disabled:opacity-50" onClick={save} disabled={busy} data-testid="lcc-save">
                    <Save size={14} /> Save &amp; publish
                </button>
            </div>
            )}
        </div>
    );
}

function FieldsEditor({ data, setData }) {
    const fields = [
        ["legal_entity_name",   "Legal entity name"],
        ["trading_name",        "Trading name"],
        ["registered_office",   "Registered office"],
        ["country",             "Country of incorporation"],
        ["company_registration","Company registration #"],
        ["tax_registration",    "Tax registration #"],
        ["governing_law",       "Governing law"],
        ["dispute_jurisdiction","Dispute jurisdiction"],
        ["support_email",       "Support email"],
        ["privacy_email",       "Privacy email"],
        ["security_email",      "Security email"],
        ["billing_email",       "Billing email"],
        ["copyright",           "Copyright line"],
    ];
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map(([k, label]) => (
                <label key={k} className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-stone-600 uppercase tracking-widest">{label}</span>
                    <input
                        className="border border-stone-200 rounded-lg px-3 py-2 text-sm"
                        value={data[k] || ""}
                        onChange={(e) => setData({ ...data, [k]: e.target.value })}
                        data-testid={`lcc-field-${k}`}
                    />
                </label>
            ))}
        </div>
    );
}

function FAQEditor({ data, setData }) {
    const items = data.items || [];
    const setItems = (next) => setData({ ...data, items: next });
    return (
        <div className="flex flex-col gap-3">
            {items.map((it, i) => (
                <div key={i} className="border border-stone-200 rounded-xl p-3 flex flex-col gap-2">
                    <input className="border border-stone-200 rounded-lg px-3 py-2 text-sm font-semibold"
                           value={it.q}
                           onChange={(e) => setItems(items.map((x, ix) => ix === i ? { ...x, q: e.target.value } : x))}
                           placeholder="Question" />
                    <textarea rows={3} className="border border-stone-200 rounded-lg px-3 py-2 text-sm"
                              value={it.a}
                              onChange={(e) => setItems(items.map((x, ix) => ix === i ? { ...x, a: e.target.value } : x))}
                              placeholder="Answer" />
                    <div className="text-right">
                        <button className="text-rose-600 text-xs" onClick={() => setItems(items.filter((_, ix) => ix !== i))}>Remove</button>
                    </div>
                </div>
            ))}
            <button className="self-start bg-stone-100 hover:bg-stone-200 rounded-full text-sm px-4 py-1.5"
                    onClick={() => setItems([...items, { q: "", a: "" }])}>+ Add FAQ</button>
        </div>
    );
}

function FooterEditor({ data, setData }) {
    const groups = ["product", "solutions", "trust", "legal", "company"];
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {groups.map((g) => {
                const rows = data[g] || [];
                return (
                    <div key={g} className="flex flex-col gap-2">
                        <div className="text-xs font-bold uppercase tracking-widest text-stone-600">{g}</div>
                        {rows.map((r, i) => (
                            <div key={i} className="flex gap-2">
                                <input className="flex-1 border border-stone-200 rounded-lg px-3 py-1.5 text-sm" value={r[0]}
                                       onChange={(e) => setData({ ...data, [g]: rows.map((x, ix) => ix === i ? [e.target.value, x[1]] : x) })} placeholder="Label" />
                                <input className="flex-1 border border-stone-200 rounded-lg px-3 py-1.5 text-sm" value={r[1]}
                                       onChange={(e) => setData({ ...data, [g]: rows.map((x, ix) => ix === i ? [x[0], e.target.value] : x) })} placeholder="/url" />
                                <button className="text-rose-500 text-xs" onClick={() => setData({ ...data, [g]: rows.filter((_, ix) => ix !== i) })}>×</button>
                            </div>
                        ))}
                        <button className="self-start text-xs text-stone-600 hover:text-stone-900"
                                onClick={() => setData({ ...data, [g]: [...rows, ["", "/"]] })}>+ Add link</button>
                    </div>
                );
            })}
        </div>
    );
}

function TestimonialRequestPanel() {
    const [f, setF] = useState({ customer_name: "", customer_email: "" });
    const [link, setLink] = useState("");
    const [busy, setBusy] = useState(false);
    const generate = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/testimonials/request-link", {
                ...f, origin: window.location.origin,
            });
            setLink(r.data.link);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to generate link");
        } finally { setBusy(false); }
    };
    const copyAndDraftEmail = () => {
        navigator.clipboard.writeText(link).catch(() => {});
        toast.success("Link copied. Opening your email client…");
        const subject = encodeURIComponent("Would you share a short VAULT testimonial?");
        const body = encodeURIComponent(
            `Hi ${f.customer_name || "there"},\n\n` +
            `Thanks for using VAULT. Would you mind sharing a short quote about your experience? ` +
            `You can submit it here — nothing is published without your explicit consent:\n\n${link}\n\n` +
            `Warmly,\nThe VAULT team`
        );
        window.open(`mailto:${f.customer_email}?subject=${subject}&body=${body}`, "_blank");
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="text-sm text-stone-600">
                Generate a private, signed link a real customer can use to submit a testimonial.
                Every submission stays unpublished until you approve it.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-stone-600 uppercase tracking-widest">Customer name</span>
                    <input className="border border-stone-200 rounded-lg px-3 py-2 text-sm"
                           value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })}
                           data-testid="tr-name" />
                </label>
                <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-stone-600 uppercase tracking-widest">Customer email</span>
                    <input type="email" className="border border-stone-200 rounded-lg px-3 py-2 text-sm"
                           value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })}
                           data-testid="tr-email" />
                </label>
            </div>
            <button className="self-start inline-flex items-center gap-2 bg-stone-900 text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-stone-800 disabled:opacity-50"
                    onClick={generate} disabled={busy || !f.customer_name || !f.customer_email}
                    data-testid="tr-generate">
                {busy ? "Generating…" : "Generate link"}
            </button>
            {link && (
                <div className="p-4 border border-stone-200 rounded-xl bg-stone-50" data-testid="tr-link-panel">
                    <div className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-2">Shareable link</div>
                    <code className="block text-xs bg-white border border-stone-200 rounded-lg px-3 py-2 break-all">{link}</code>
                    <button className="mt-3 inline-flex items-center gap-2 bg-emerald-600 text-white rounded-full px-4 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                            onClick={copyAndDraftEmail}
                            data-testid="tr-copy-email">
                        Copy link + Draft email
                    </button>
                </div>
            )}
        </div>
    );
}

function ConsentAuditPanel() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const r = await api.get("/admin/testimonials/consent-audit");
            setRows(r.data?.rows || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load audit");
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/testimonials/run-reminders");
            toast.success(`Reminder pass: scanned ${r.data.scanned}, sent ${r.data.sent}, skipped ${r.data.skipped_recent}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to run reminders");
        } finally { setBusy(false); }
    };

    const tone = (days) => {
        if (days == null) return "text-stone-500";
        if (days < 0) return "text-rose-600";
        if (days <= 7) return "text-amber-600";
        return "text-emerald-600";
    };

    return (
        <div data-testid="consent-audit-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-stone-600 max-w-2xl">
                    <ShieldCheck size={13} className="inline mr-1 text-emerald-600" />
                    Every published testimonial tracks its 90-day consent window. This audit shows
                    who renewed, when, and from which IP — the compliance trail regulators expect.
                </div>
                <div className="flex gap-2">
                    <button className="text-xs bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                            onClick={load} disabled={busy} data-testid="ca-reload">
                        <RefreshCcw size={12} /> Reload
                    </button>
                    <button className="text-xs bg-stone-800 hover:bg-stone-900 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                            onClick={async () => {
                                try {
                                    const resp = await api.get("/admin/testimonials/consent-audit.csv", { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
                                    const a = document.createElement("a");
                                    a.href = url; a.download = "vault-consent-audit.csv";
                                    document.body.appendChild(a); a.click(); a.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success("CSV downloaded.");
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || "CSV export failed");
                                }
                            }}
                            data-testid="ca-download-csv">
                        <Download size={12} /> Download CSV
                    </button>
                    <button className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                            onClick={runNow} disabled={busy} data-testid="ca-run-reminders">
                        <Send size={12} /> Run reminders now
                    </button>
                </div>
            </div>
            {rows.length === 0 && !busy && (
                <div className="text-sm text-stone-500 p-6 border border-dashed border-stone-200 rounded-xl text-center">
                    No published testimonials yet. Consent tracking activates once you publish your first quote.
                </div>
            )}
            {rows.length > 0 && (
                <div className="border border-stone-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
                            <tr>
                                <th className="text-left p-3">Customer</th>
                                <th className="text-left p-3">Approved</th>
                                <th className="text-left p-3">Last renewed</th>
                                <th className="text-left p-3">Expires</th>
                                <th className="text-left p-3">Reminder sent</th>
                                <th className="text-right p-3">History</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <React.Fragment key={r.id}>
                                <tr className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`ca-row-${r.id}`}>
                                    <td className="p-3">
                                        <div className="font-medium text-stone-900">{r.customer_name || "—"}</div>
                                        <div className="text-[11px] text-stone-500 truncate max-w-[240px]">{r.quote_snippet}…</div>
                                    </td>
                                    <td className="p-3 text-stone-700 tabular-nums text-xs">{(r.approval_date || "").slice(0, 10) || "—"}</td>
                                    <td className="p-3 text-stone-700 tabular-nums text-xs">{(r.consent_renewed_at || "").slice(0, 10) || "—"}</td>
                                    <td className={`p-3 tabular-nums text-xs ${tone(r.days_left)}`}>
                                        {(r.expires_at || "").slice(0, 10) || "—"}
                                        {r.days_left != null && (
                                            <span className="ml-2 text-[11px]">
                                                {r.days_left < 0 ? `${Math.abs(r.days_left)}d overdue`
                                                    : r.days_left === 0 ? "today"
                                                    : `${r.days_left}d left`}
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 text-stone-700 tabular-nums text-xs">{(r.reminder_last_sent_at || "").slice(0, 10) || "—"}</td>
                                    <td className="p-3 text-right">
                                        <button className="text-[11px] text-stone-700 underline hover:no-underline"
                                                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                                data-testid={`ca-expand-${r.id}`}>
                                            {r.history.length} event{r.history.length !== 1 ? "s" : ""}
                                        </button>
                                    </td>
                                </tr>
                                {expanded === r.id && (
                                    <tr className="bg-stone-50/70">
                                        <td colSpan={6} className="p-4">
                                            {r.history.length === 0 ? (
                                                <div className="text-xs text-stone-500 italic">No renewal events yet. Awaiting the first customer click.</div>
                                            ) : (
                                                <div className="grid gap-2">
                                                    {r.history.map((h, i) => (
                                                        <div key={i} className="grid grid-cols-[auto_1fr] gap-3 text-xs">
                                                            <span className="text-stone-500 tabular-nums">{(h.renewed_at || "").slice(0, 19).replace("T", " ")}</span>
                                                            <span className="text-stone-800">
                                                                Renewed from <code className="bg-stone-100 px-1.5 py-0.5 rounded">{h.ip || "unknown"}</code>
                                                                {h.user_agent && <span className="text-stone-500"> · {h.user_agent.slice(0, 60)}</span>}
                                                                <span className="ml-2 text-emerald-700">
                                                                    → until {(h.expires_at || "").slice(0, 10)}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function CookieEditor({ data, setData }) {
    const cats = data.categories || [];
    return (
        <div className="flex flex-col gap-3">
            {cats.map((c, i) => (
                <div key={c.key} className="border border-stone-200 rounded-xl p-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                    <div className="text-sm font-semibold">{c.label}</div>
                    <input className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm md:col-span-2"
                           value={c.desc}
                           onChange={(e) => setData({ ...data, categories: cats.map((x, ix) => ix === i ? { ...x, desc: e.target.value } : x) })}
                           placeholder="Description shown to visitors" />
                </div>
            ))}
            <div className="text-xs text-stone-500">Adding new categories requires an app update — descriptions can be edited safely here.</div>
        </div>
    );
}
